<script setup lang="ts">
import { FieldItem, ValidateRule } from '@/coreDomainsModule/constant/validateRules';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import { useForms } from '@/corePagesModule/composables/useForm';
import { i18n } from '@/i18n';
import { ORDER_GROUP_MODIFY_KEYS, OrderGroupModalState } from '@/orderTableModule/constants';
import { OrderGroupFactory } from '@/coreDomainsModule/factories/OrderGroupFactory';
import { useOrderTableStore } from '@/orderTableModule/pages/useOrderTableStore';
import TextInput from '@/unmanagedBusinessModule/components/TextInput.vue';
import { storeToRefs } from 'pinia';
import { computed, h, ref, watch } from 'vue';
import OrderGroupSelectInput from './OrderGroupSelectInput.vue';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import OrderGroupInfoSection from '@/corePagesModule/components/OrderGroup/OrderGroupInfoSection.vue';

const t = i18n.t;
const store = useOrderTableStore();
const { state } = storeToRefs(store);
const { onNextCreateOrderGroup, onNextEditOrderGroup } = store;

const isEdited = ref(false);
const editTargetOrderGroupName = ref('');

const fieldItems = ((): FieldItem[] => {
  const items = ORDER_GROUP_MODIFY_KEYS.map(([name, rules]) => ({ name, rules }));
  return items;
})();

const { fieldState, allValidate, resetForm } = useForms(fieldItems);

const isOpen = computed(
  () =>
    state.value.orderGroupState.openingModal === OrderGroupModalState.OrderGroupNew ||
    state.value.orderGroupState.openingModal === OrderGroupModalState.OrderGroupInfoEdit
);

const isEditMode = computed(() => state.value.orderGroupState.openingModal === OrderGroupModalState.OrderGroupInfoEdit);

const titleText = computed(() => (isEditMode.value ? t('発注グループの編集') : t('発注グループの新規作成')));
const descriptionText = computed(() =>
  isEditMode.value
    ? t('編集する発注グループ名を選択し、新しいグループ名で保存してください。')
    : t('発注をまとめる任意のグループを作成します。')
);
const nextButtonLabel = computed(() => (isEditMode.value ? t('保存') : t('新規作成')));

const targetOrderGroupData = computed(() =>
  state.value.orderGroupState.groupList.find(
    (group) => group.orderGroupName === (editTargetOrderGroupName.value || state.value.orderGroupState.targetGroupName)
  )
);

watch(isOpen, (val) => {
  if (val && isEditMode) {
    editTargetOrderGroupName.value = state.value.orderGroupState.targetGroupName;
    setDefaultFieldValue();
  } else if (!val) {
    onBack();
  }
});

/**
 * backボタンを押した際の動作
 */
const onBack = () => {
  resetForm(fieldItems);
  isEdited.value = false;
  editTargetOrderGroupName.value = '';
  state.value.orderGroupState.openingModal = OrderGroupModalState.None;
};

/**
 * nextボタンを押した際の動作
 */
const onNext = async () => {
  if (isEditMode.value) {
    const editedUpperLimitQuantity = Number(fieldState['upperLimitQuantity'].value.value);
    if (!targetOrderGroupData.value || isNaN(editedUpperLimitQuantity)) {
      // 編集モード中で、対象のグループデータがない場合、上限数量がNaNな場合
      await showInfoModal({
        modalTitle: t('エラー'),
        modalDescription: t('発注グループ編集中にエラーが発生しました。時間をおいて再度お試しください。'),
        backButtonLabel: 'OK',
        icon: 'error',
        colorStyle: 'error',
        enableOutsideClick: false,
        isDisabledNextButton: true,
      });
      return;
    } else if (editedUpperLimitQuantity < targetOrderGroupData.value.totalLinkedOrderAmount) {
      // 現在発注グループに紐づいている発注の合計数量を編集した上限数量が下回っている場合
      await showUpperLimitQuantityLessThanTotalAmountModal();
      return;
    }
  }
  state.value.orderGroupState.editingGroup = OrderGroupFactory.create(
    ORDER_GROUP_MODIFY_KEYS.reduce((acc, [key]) => {
      acc[key] = fieldState[key].value.value;
      return acc;
    }, {} as Record<(typeof ORDER_GROUP_MODIFY_KEYS)[number][0], string>)
  );
  isEditMode.value ? await onNextEditOrderGroup(editTargetOrderGroupName.value) : await onNextCreateOrderGroup();
};

/**
 * フォームに初期値を挿入する
 */
const setDefaultFieldValue = () => {
  const orderGroup = targetOrderGroupData.value;
  if (!isEditMode.value || !orderGroup) return;
  ORDER_GROUP_MODIFY_KEYS.forEach(([name, _]) => {
    fieldState[name].setValue(orderGroup[name]);
  });
};

/**
 * 対象の発注グループを変更した際の処理
 * @param name - 発注グループ名
 */
const onChangeSelectInput = (name: string) => {
  editTargetOrderGroupName.value = name;
  setDefaultFieldValue();
};

/**
 * 編集後の上限数量が合計数量よりも少なくなっていることを通知するモーダルを表示する
 */
const showUpperLimitQuantityLessThanTotalAmountModal = async () =>
  targetOrderGroupData.value &&
  (await showInfoModal({
    modalTitle: t('発注グループの上限数量オーバー'),
    modalDescription: t('発注グループの上限数量の引き上げ、または各発注の数量を上限数量に収まるようにしてください。'),
    modalContent: h(OrderGroupInfoSection, {
      orderGroupName: targetOrderGroupData.value.orderGroupName,
      numberOfOrder: targetOrderGroupData.value.linkedOrderCount,
      upperLimitQuantity: targetOrderGroupData.value.upperLimitQuantity,
      afterUpperLimitQuantity: Number(fieldState['upperLimitQuantity'].value.value),
      totalAmount: targetOrderGroupData.value.totalLinkedOrderAmount,
      hasBackground: true,
    }),
    backButtonLabel: 'OK',
    icon: 'error',
    colorStyle: 'error',
    enableOutsideClick: false,
    isDisabledNextButton: true,
    modalWidth: 764,
  }));
</script>
<template>
  <InfoModal
    :modal-state="isOpen"
    :back-button-label="t('キャンセル')"
    :next-button-label="nextButtonLabel"
    enable-outside-click
    :modal-width="764"
    :is-disabled-next-button="isEdited && !allValidate()"
    @on-next="onNext"
    @on-back="onBack"
  >
    <template #title>
      {{ titleText }}
    </template>
    <template #description>
      {{ descriptionText }}
    </template>
    <template #default>
      <div class="order-group-create-modal-content">
        <OrderGroupSelectInput
          v-if="isEditMode"
          no-fetch
          :index-number="2"
          use-unique-model-value
          :prop-model-value="editTargetOrderGroupName"
          :label="t('編集する発注グループを選択してください')"
          @change="onChangeSelectInput"
        />
        <div v-for="[name, rules] in ORDER_GROUP_MODIFY_KEYS" :key="name">
          <TextInput
            :model-value="fieldState[name].value.value"
            :label="`${t(name)}${rules.includes(ValidateRule.Required) ? '*' : ''}`"
            :placeholder="t(name)"
            :is-error="fieldState[name].isError.value"
            :error-message="fieldState[name].errorMessage.value"
            @update:model-value="
              (val) => {
                fieldState[name].setValue(val, true);
                isEdited = true;
              }
            "
          />
        </div>
      </div>
    </template>
  </InfoModal>
</template>
<style lang="scss" scoped>
.order-group-create-modal-content {
  background-color: #f5f5f5;
  padding: 16px;
  border-radius: 8px;
}
</style>
